import { Link, useRouter } from "@tanstack/react-router";
import { Box, Button, Heading, Icon, Stack, Text } from "@chakra-ui/react";
import { LuArrowLeft, LuLockKeyhole, LuRotateCw, LuTriangleAlert } from "react-icons/lu";
import { PageHeader } from "./PageHeader";

/**
 * Whether a thrown error is the server refusing, rather than failing.
 *
 * Matched on the message because that is what a Convex error carries to the
 * client: the thrown `Error` is serialised, so there is no class to check and
 * no code field to read. Deliberately broad — several call sites word this
 * refusal differently, and treating an unrecognised refusal as a crash is the
 * worse mistake.
 */
function isAuthorizationError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? "");
  return /not authori[sz]ed|not allowed|forbidden/i.test(message);
}

/**
 * What a route shows when its data could not be loaded.
 *
 * Convex's `useQuery` throws, so a refusal from the server arrives here as a
 * render error rather than as a value a page could handle. Without this the
 * reader gets the raw text — request id, stack frame and all — which says
 * nothing they can act on and looks like the application broke.
 *
 * Refusal and failure are told apart, because they call for different things:
 * one is a door that is closed to you, the other is worth retrying.
 */
export function RouteError({
  error,
  componentStack,
}: {
  error: unknown;
  /**
   * Which components were rendering when it failed.
   *
   * Worth showing, not hiding: "element type is invalid" names a symptom and
   * the stack names the place. Without it the reader can only report that
   * something broke, and whoever has to fix it starts from nothing.
   */
  componentStack?: string;
}) {
  const router = useRouter();
  const refused = isAuthorizationError(error);

  return (
    <Box minH="full" bg="bg.subtle">
      <PageHeader
        title={refused ? "Not your activity" : "Something went wrong"}
        description={refused
          ? "This page belongs to somebody else."
          : "The page could not be loaded."}
        breadcrumbs={[{ label: "CeMMbase", to: "/" }]}
      />

      <Box px={{ base: 6, sm: 8, lg: 10 }} py={{ base: 8, lg: 12 }}>
        <Stack gap={5} maxW="xl">
          <Icon
            as={refused ? LuLockKeyhole : LuTriangleAlert}
            boxSize={8}
            color={refused ? "fg.muted" : "orange.500"}
          />

          <Stack gap={2}>
            <Heading size="lg">
              {refused
                ? "You do not have access to this"
                : "That did not load"}
            </Heading>
            <Text fontSize="sm" color="fg.muted">
              {refused
                ? "Activities are visible to the person they belong to, their supervisor, anyone asked to act on a step of them, and the activity admins. If you think you should be on that list, ask an activity admin."
                : "This is usually temporary. Trying again often works; if it does not, the message below is worth passing on."}
            </Text>
          </Stack>

          {/* The raw text still matters to whoever has to fix it, so it is
              kept — just no longer the first thing anybody reads. */}
          {!refused ? (
            <Box
              borderWidth="1px"
              borderColor="border"
              bg="bg.muted"
              px={4}
              py={3}
              fontSize="xs"
              fontFamily="mono"
              color="fg.muted"
              overflowX="auto"
            >
              {error instanceof Error ? error.message : String(error)}
              {componentStack ? (
                <Box as="pre" mt={3} whiteSpace="pre-wrap" opacity={0.75}>
                  {componentStack.trim().split(/\r?\n/).slice(0, 12).join("\n")}
                </Box>
              ) : null}
            </Box>
          ) : null}

          <Stack direction="row" gap={3} flexWrap="wrap">
            <Link to="/">
              <Button variant="outline" size="sm">
                <LuArrowLeft />
                Back to the dashboard
              </Button>
            </Link>
            {!refused ? (
              <Button size="sm" colorPalette="blue" onClick={() => void router.invalidate()}>
                <LuRotateCw />
                Try again
              </Button>
            ) : null}
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}
